'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { 
  Target, 
  Users, 
  Heart, 
  TrendingUp, 
  MessageSquare, 
  Lightbulb,
  Save,
  Wand2,
  Plus,
  Trash2,
  CheckCircle
} from 'lucide-react'
import { mockStrategyService, Brand } from '../../utils/mockData'

interface BrandStrategyTabProps {
  brand: Brand
}

interface StrategyData {
  mission?: string
  vision?: string
  values?: string[]
  targetAudience?: {
    primary?: string
    secondary?: string
    demographics?: string
  }
  positioning?: {
    statement?: string
    differentiators?: string[]
    competitors?: string[]
  }
  personality?: {
    traits?: string[]
    tone?: string
    voice?: string
  }
  goals?: {
    shortTerm?: string[]
    longTerm?: string[]
    metrics?: string[]
  }
}

export function BrandStrategyTab({ brand }: BrandStrategyTabProps) {
  const [strategyData, setStrategyData] = useState<StrategyData>({
    mission: '',
    vision: '',
    values: [],
    targetAudience: {
      primary: '',
      secondary: '',
      demographics: ''
    },
    positioning: {
      statement: '',
      differentiators: [],
      competitors: []
    },
    personality: {
      traits: [],
      tone: '',
      voice: ''
    },
    goals: {
      shortTerm: [],
      longTerm: [],
      metrics: []
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [newValue, setNewValue] = useState('')
  const [newDifferentiator, setNewDifferentiator] = useState('')
  const [newCompetitor, setNewCompetitor] = useState('')
  const [newTrait, setNewTrait] = useState('')
  const [newShortTermGoal, setNewShortTermGoal] = useState('')
  const [newLongTermGoal, setNewLongTermGoal] = useState('')
  const [newMetric, setNewMetric] = useState('')

  useEffect(() => {
    loadStrategyData()
  }, [brand.id])

  const loadStrategyData = async () => {
    setIsLoading(true)
    try {
      const { strategy } = await mockStrategyService.getStrategy(brand.id)
      if (strategy) {
        setStrategyData(strategy)
      }
    } catch (error) {
      console.error('Error loading strategy data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveStrategyData = async () => {
    setIsSaving(true)
    try {
      await mockStrategyService.saveStrategy(brand.id, strategyData)
      setLastSaved(new Date())
    } catch (error) {
      console.error('Error saving strategy data:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const updateField = (field: keyof StrategyData, value: any) => {
    setStrategyData(prev => ({ ...prev, [field]: value }))
  }

  const updateNestedField = (section: keyof StrategyData, field: string, value: any) => {
    setStrategyData(prev => ({
      ...prev,
      [section]: {
        ...prev[section] as any,
        [field]: value
      }
    }))
  }

  const addToArray = (section: keyof StrategyData, field: string, value: string, setValue: (val: string) => void) => {
    if (!value.trim()) return
    
    const currentSection = strategyData[section] as any
    const currentArray = currentSection?.[field] || []
    
    updateNestedField(section, field, [...currentArray, value.trim()])
    setValue('')
  }

  const removeFromArray = (section: keyof StrategyData, field: string, index: number) => {
    const currentSection = strategyData[section] as any
    const currentArray = currentSection?.[field] || []
    const newArray = currentArray.filter((_: any, i: number) => i !== index)
    
    updateNestedField(section, field, newArray)
  }

  const generateAISuggestions = async (section: string) => {
    // Mock AI suggestions based on brand data
    const suggestions = {
      mission: `To provide innovative ${brand.metadata?.industry?.toLowerCase() || 'technology'} solutions that empower ${brand.metadata?.target_audience?.toLowerCase() || 'businesses'} to achieve their goals.`,
      vision: `To be the leading ${brand.metadata?.industry?.toLowerCase() || 'technology'} partner for ${brand.metadata?.target_audience?.toLowerCase() || 'businesses'} worldwide.`,
      values: ['Innovation', 'Reliability', 'Customer Focus', 'Quality', 'Integrity'],
      positioning: `${brand.name} delivers cutting-edge solutions that combine innovation with reliability, setting us apart from traditional providers.`,
      differentiators: ['AI-powered technology', 'Personalized service', 'Proven track record', 'Innovative approach'],
      competitors: ['Company A', 'Company B', 'Industry Leader'],
      traits: ['Professional', 'Innovative', 'Trustworthy', 'Approachable', 'Expert'],
      shortTermGoals: ['Increase brand awareness', 'Launch new product line', 'Expand customer base'],
      longTermGoals: ['Become market leader', 'International expansion', 'Achieve sustainability goals'],
      metrics: ['Brand awareness %', 'Customer satisfaction', 'Market share', 'Revenue growth']
    }

    return suggestions[section as keyof typeof suggestions] || []
  }

  const handleAISuggest = async (section: string, field?: string) => {
    const suggestions = await generateAISuggestions(section)
    
    if (Array.isArray(suggestions)) {
      if (field) {
        updateNestedField(section as keyof StrategyData, field, suggestions)
      } else {
        updateField(section as keyof StrategyData, suggestions)
      }
    } else {
      if (field) {
        updateNestedField(section as keyof StrategyData, field, suggestions)
      } else {
        updateField(section as keyof StrategyData, suggestions)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 bg-blue-600 rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-slate-600">Loading strategy data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Brand Strategy</h2>
          <p className="text-slate-600">Define your brand's core purpose, values, and positioning</p>
        </div>
        <div className="flex items-center space-x-3">
          {lastSaved && (
            <div className="flex items-center space-x-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>Saved {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
          <Button 
            onClick={saveStrategyData}
            disabled={isSaving}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Strategy'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mission & Vision */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Mission & Vision
            </CardTitle>
            <CardDescription>
              Define your brand's purpose and future aspirations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="mission">Mission Statement</Label>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleAISuggest('mission')}
                  className="text-xs"
                >
                  <Wand2 className="w-3 h-3 mr-1" />
                  AI Suggest
                </Button>
              </div>
              <Textarea
                id="mission"
                placeholder="What is your brand's core purpose?"
                value={strategyData.mission || ''}
                onChange={(e) => updateField('mission', e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="vision">Vision Statement</Label>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleAISuggest('vision')}
                  className="text-xs"
                >
                  <Wand2 className="w-3 h-3 mr-1" />
                  AI Suggest
                </Button>
              </div>
              <Textarea
                id="vision"
                placeholder="Where do you see your brand in the future?"
                value={strategyData.vision || ''}
                onChange={(e) => updateField('vision', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Brand Values */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5" />
              Brand Values
            </CardTitle>
            <CardDescription>
              Core principles that guide your brand
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex space-x-2">
              <Input
                placeholder="Add a brand value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addToArray('values', '', newValue, setNewValue)}
              />
              <Button 
                size="sm" 
                onClick={() => addToArray('values', '', newValue, setNewValue)}
              >
                <Plus className="w-4 h-4" />
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => handleAISuggest('values')}
              >
                <Wand2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(strategyData.values || []).map((value, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {value}
                  <button
                    onClick={() => removeFromArray('values', '', index)}
                    className="ml-1 hover:text-red-500"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Target Audience */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Target Audience
            </CardTitle>
            <CardDescription>
              Define who your brand serves
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="primaryAudience">Primary Audience</Label>
              <Input
                id="primaryAudience"
                placeholder="Who is your main target audience?"
                value={strategyData.targetAudience?.primary || ''}
                onChange={(e) => updateNestedField('targetAudience', 'primary', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secondaryAudience">Secondary Audience</Label>
              <Input
                id="secondaryAudience"
                placeholder="Who else might be interested?"
                value={strategyData.targetAudience?.secondary || ''}
                onChange={(e) => updateNestedField('targetAudience', 'secondary', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="demographics">Demographics</Label>
              <Textarea
                id="demographics"
                placeholder="Age, location, income, interests, etc."
                value={strategyData.targetAudience?.demographics || ''}
                onChange={(e) => updateNestedField('targetAudience', 'demographics', e.target.value)}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Brand Positioning */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Brand Positioning
            </CardTitle>
            <CardDescription>
              How you differentiate from competitors
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="positioning">Positioning Statement</Label>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleAISuggest('positioning')}
                  className="text-xs"
                >
                  <Wand2 className="w-3 h-3 mr-1" />
                  AI Suggest
                </Button>
              </div>
              <Textarea
                id="positioning"
                placeholder="What makes you unique in the market?"
                value={strategyData.positioning?.statement || ''}
                onChange={(e) => updateNestedField('positioning', 'statement', e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Key Differentiators</Label>
              <div className="flex space-x-2">
                <Input
                  placeholder="Add a differentiator"
                  value={newDifferentiator}
                  onChange={(e) => setNewDifferentiator(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addToArray('positioning', 'differentiators', newDifferentiator, setNewDifferentiator)}
                />
                <Button 
                  size="sm" 
                  onClick={() => addToArray('positioning', 'differentiators', newDifferentiator, setNewDifferentiator)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleAISuggest('differentiators')}
                >
                  <Wand2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(strategyData.positioning?.differentiators || []).map((diff, index) => (
                  <Badge key={index} variant="outline" className="flex items-center gap-1">
                    {diff}
                    <button
                      onClick={() => removeFromArray('positioning', 'differentiators', index)}
                      className="ml-1 hover:text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Brand Personality */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Brand Personality & Voice
            </CardTitle>
            <CardDescription>
              Define how your brand communicates and feels
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Personality Traits</Label>
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Add a personality trait"
                      value={newTrait}
                      onChange={(e) => setNewTrait(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addToArray('personality', 'traits', newTrait, setNewTrait)}
                    />
                    <Button 
                      size="sm" 
                      onClick={() => addToArray('personality', 'traits', newTrait, setNewTrait)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleAISuggest('traits')}
                    >
                      <Wand2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(strategyData.personality?.traits || []).map((trait, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        {trait}
                        <button
                          onClick={() => removeFromArray('personality', 'traits', index)}
                          className="ml-1 hover:text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tone">Brand Tone</Label>
                  <Input
                    id="tone"
                    placeholder="e.g., Professional, Friendly, Authoritative"
                    value={strategyData.personality?.tone || ''}
                    onChange={(e) => updateNestedField('personality', 'tone', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="voice">Brand Voice</Label>
                  <Textarea
                    id="voice"
                    placeholder="Describe how your brand communicates..."
                    value={strategyData.personality?.voice || ''}
                    onChange={(e) => updateNestedField('personality', 'voice', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Strategic Goals */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              Strategic Goals
            </CardTitle>
            <CardDescription>
              Set objectives and define success metrics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label>Short-term Goals (6-12 months)</Label>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Add short-term goal"
                    value={newShortTermGoal}
                    onChange={(e) => setNewShortTermGoal(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('goals', 'shortTerm', newShortTermGoal, setNewShortTermGoal)}
                  />
                  <Button 
                    size="sm" 
                    onClick={() => addToArray('goals', 'shortTerm', newShortTermGoal, setNewShortTermGoal)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-1">
                  {(strategyData.goals?.shortTerm || []).map((goal, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                      <span>{goal}</span>
                      <button
                        onClick={() => removeFromArray('goals', 'shortTerm', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Long-term Goals (1-3 years)</Label>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Add long-term goal"
                    value={newLongTermGoal}
                    onChange={(e) => setNewLongTermGoal(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('goals', 'longTerm', newLongTermGoal, setNewLongTermGoal)}
                  />
                  <Button 
                    size="sm" 
                    onClick={() => addToArray('goals', 'longTerm', newLongTermGoal, setNewLongTermGoal)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-1">
                  {(strategyData.goals?.longTerm || []).map((goal, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                      <span>{goal}</span>
                      <button
                        onClick={() => removeFromArray('goals', 'longTerm', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Success Metrics</Label>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Add success metric"
                    value={newMetric}
                    onChange={(e) => setNewMetric(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('goals', 'metrics', newMetric, setNewMetric)}
                  />
                  <Button 
                    size="sm" 
                    onClick={() => addToArray('goals', 'metrics', newMetric, setNewMetric)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-1">
                  {(strategyData.goals?.metrics || []).map((metric, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                      <span>{metric}</span>
                      <button
                        onClick={() => removeFromArray('goals', 'metrics', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}